import React, { useMemo, useState } from 'react';
import {
  ADD_FIELD_INTENTS,
  BEHAVIOUR_TYPES,
  defaultBusinessConfig,
  uniqueCode,
  suggestFieldCode,
  compileField,
  humanReadableFormula,
} from '../adapters';

const AddFieldWizard = ({ open, sectionCode, sections, existingCodes, onClose, onSubmit }) => {
  const [step, setStep] = useState(1);
  const [intent, setIntent] = useState('MANUAL');
  const [name, setName] = useState('');
  const [targetSection, setTargetSection] = useState(sectionCode || '');
  const [rateValue, setRateValue] = useState(2);
  const [baseFieldCode, setBaseFieldCode] = useState('');

  const intentDef = ADD_FIELD_INTENTS.find((i) => i.id === intent) || ADD_FIELD_INTENTS[0];

  const previewField = useMemo(() => {
    const fieldCode = uniqueCode(suggestFieldCode(name) || intent, existingCodes);
    const behaviourType = intentDef.behaviourType;
    const cfg = defaultBusinessConfig({
      behaviourType,
      rateValue: behaviourType === BEHAVIOUR_TYPES.PERCENTAGE || behaviourType === BEHAVIOUR_TYPES.BALANCE_RECOVERY ? rateValue : null,
      baseFieldCode: baseFieldCode || null,
      financialEffect: intent === 'RECOVERY' || intent === 'PERCENTAGE' || intent === 'ADVANCE_RECOVERY' ? 'DEDUCT' : 'INFORMATION',
      includeInGroup: intent === 'RECOVERY' || intent === 'PERCENTAGE' || intent === 'ADVANCE_RECOVERY' ? 'TOTAL_RECOVERIES' : intent === 'TOTAL' ? 'TOTAL_RECOVERIES' : 'NONE',
      financialClassification: intent === 'WITHHELD' ? 'WITHHELD' : intent === 'RECOVERY' || intent === 'PERCENTAGE' ? 'STATUTORY_RECOVERY' : 'REFERENCE',
    });
    return compileField({
      fieldCode,
      fieldLabel: name || 'New Field',
      sectionCode: targetSection || sectionCode || sections?.[0]?.sectionCode || 'WORK_VALUE',
      sequence: 0,
      dataType: intent === 'REFERENCE' ? 'TEXT' : 'AMOUNT',
      fieldType: 'INPUT',
      sourceType: 'MANUAL',
      semanticRole: null,
      formulaJson: null,
      editable: true,
      visible: true,
      printVisible: true,
      allowOverride: false,
      status: 'ACTIVE',
      required: false,
      roundingMethod: 'ROUND',
      decimalPlaces: 0,
      metadata: { businessConfig: cfg },
    });
  }, [name, intent, intentDef, rateValue, baseFieldCode, targetSection, sectionCode, sections, existingCodes]);

  if (!open) return null;

  const close = () => {
    setStep(1);
    setIntent('MANUAL');
    setName('');
    onClose();
  };

  return (
    <div className="ra-tpl-modal-overlay" onClick={close}>
      <div className="ra-tpl-modal ra-tpl-modal--wide" onClick={(e) => e.stopPropagation()}>
        <h3>Add Field — Step {step} of 5</h3>
        {step === 1 && (
          <div className="ra-tpl-intent-grid">
            {ADD_FIELD_INTENTS.map((i) => (
              <button key={i.id} type="button" className={`ra-tpl-intent-card ${intent === i.id ? 'is-active' : ''}`} onClick={() => setIntent(i.id)}>
                {i.label}
              </button>
            ))}
          </div>
        )}
        {step === 2 && (
          <div className="form-group">
            <label>Field Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="GST TDS" autoFocus />
            <p className="ra-tpl-help">Code will be suggested as {suggestFieldCode(name) || '…'}</p>
          </div>
        )}
        {step === 3 && (
          <div>
            {(intentDef.behaviourType === BEHAVIOUR_TYPES.PERCENTAGE || intentDef.behaviourType === BEHAVIOUR_TYPES.BALANCE_RECOVERY) && (
              <div className="form-row">
                <div className="form-group">
                  <label>Rate (%)</label>
                  <input type="number" step="0.01" value={rateValue} onChange={(e) => setRateValue(Number(e.target.value))} />
                </div>
                <div className="form-group">
                  <label>Calculated On (field code)</label>
                  <input value={baseFieldCode} onChange={(e) => setBaseFieldCode(e.target.value)} placeholder="NET_EXCL_SECURED_ADVANCE" />
                </div>
              </div>
            )}
            {intentDef.behaviourType === BEHAVIOUR_TYPES.MANUAL_ENTRY && (
              <p className="ra-tpl-help">This will be a manual amount entered on each RA Bill.</p>
            )}
            {intentDef.behaviourType === BEHAVIOUR_TYPES.CUSTOM_FORMULA && (
              <p className="ra-tpl-help">You can finish the formula in the Calculation tab after adding.</p>
            )}
          </div>
        )}
        {step === 4 && (
          <div className="form-group">
            <label>Where should it appear?</label>
            <select value={targetSection || sectionCode || ''} onChange={(e) => setTargetSection(e.target.value)}>
              {(sections || []).map((s) => <option key={s.sectionCode} value={s.sectionCode}>{s.sectionName}</option>)}
            </select>
          </div>
        )}
        {step === 5 && (
          <div className="ra-tpl-summary-card">
            <strong>{previewField.fieldLabel}</strong>
            <p>Code: {previewField.fieldCode}</p>
            <p>Section: {previewField.sectionCode}</p>
            <p>{humanReadableFormula(previewField, [])}</p>
          </div>
        )}
        <div className="ra-tpl-modal-actions">
          <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
          {step > 1 && <button type="button" className="btn-secondary" onClick={() => setStep((s) => s - 1)}>Back</button>}
          {step < 5 ? (
            <button
              type="button"
              className="btn-primary"
              disabled={step === 2 && !name.trim()}
              onClick={() => setStep((s) => s + 1)}
            >
              Next
            </button>
          ) : (
            <button type="button" className="btn-primary" onClick={() => { onSubmit(previewField); close(); }}>Add Field</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddFieldWizard;
